// client/src/components/settings/BrandPanel.jsx
// 贴牌面板(用户级): 品牌标题 + 自定义背景图, 仅改自己, 仅自己登录后可见
// 被 SystemSettings(超管) 和 BrandSettings(VIP代理) 复用
import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import { FiSave, FiImage, FiEye, FiLink } from 'react-icons/fi';
import axios from 'axios';

import Button from '../common/Button';
import { useAuth } from '../../context/AuthContext';
import { compressImage, dataUrlBytes, formatBytes } from '../../utils/image';

// 保存个人贴牌(PUT /api/auth/brand)
const updateBrand = async (data) => {
  const response = await axios.put('/api/auth/brand', data);
  return response.data;
};

const BrandPanel = () => {
  const fileInputRef = useRef(null);
  const { currentUser, refreshUser } = useAuth();

  const [brandTitle, setBrandTitle] = useState('');
  const [bgUrl, setBgUrl] = useState(''); // dataURL 或 http(s)URL, 空=无个人背景
  const [bgPreview, setBgPreview] = useState('');

  // 初始化为当前用户的贴牌数据
  useEffect(() => {
    if (currentUser) {
      setBrandTitle(currentUser.brandTitle || '');
      if (currentUser.backgroundImage) {
        setBgUrl(currentUser.backgroundImage);
        setBgPreview(currentUser.backgroundImage);
      }
    }
  }, [currentUser]);

  const brandMutation = useMutation(updateBrand, {
    onSuccess: (_, vars) => {
      toast.success(vars.brandTitle !== undefined ? '品牌标题已更新' : '背景图已更新');
      refreshUser();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || '保存失败');
    }
  });

  const handleSaveBrandTitle = () => {
    brandMutation.mutate({ brandTitle: brandTitle.trim() });
  };

  const handleSaveBackground = () => {
    const v = bgUrl.trim();
    if (v && !/^https?:\/\/\S+$/i.test(v) && !v.startsWith('data:image/')) {
      toast.error('背景图只支持上传的图片或 http(s) 图片链接');
      return;
    }
    brandMutation.mutate({ backgroundImage: v });
  };

  // 上传: 前端自动压缩到最长边1920/JPEG85%
  const handleBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (e.target) e.target.value = ''; // 允许重复选同一文件

    if (file.size > 8 * 1024 * 1024) {
      toast.error('原图不能超过8MB(上传后会自动压缩)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件');
      return;
    }

    try {
      const dataUrl = await compressImage(file, { maxDim: 1920, quality: 0.85 });
      const bytes = dataUrlBytes(dataUrl);
      if (bytes > 4 * 1024 * 1024) {
        toast.error(`压缩后仍达 ${formatBytes(bytes)}(上限4MB), 请换更小的图`);
        return;
      }
      setBgUrl(dataUrl);
      setBgPreview(dataUrl);
      toast.success(`图片已压缩至 ${formatBytes(bytes)}, 点击"保存背景图"生效`);
    } catch (err) {
      toast.error(err.message || '图片处理失败');
    }
  };

  const brandSaving = brandMutation.isLoading;

  return (
    <div className="space-y-8">
      {/* 品牌标题: 显示在侧边栏品牌位, 优先于平台标题 */}
      <div>
        <label className="block text-[#57606a] mb-2">品牌标题</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={brandTitle}
            onChange={(e) => setBrandTitle(e.target.value)}
            placeholder="留空 = 跟随平台默认"
            maxLength={100}
            className="flex-1 bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none brand-ring"
          />
          <Button
            type="button"
            variant="primary"
            icon={<FiSave />}
            onClick={handleSaveBrandTitle}
            disabled={brandSaving}
          >
            {brandSaving ? '保存中...' : '保存标题'}
          </Button>
        </div>
        <p className="text-sm text-[#8b949e] mt-2">
          显示在左侧导航栏顶部的品牌位(最多100字), 仅您自己登录后可见。留空则显示平台默认标题
        </p>
      </div>

      {/* 自定义背景图: 上传(自动压缩) 或 图片链接 */}
      <div>
        <label className="block text-[#57606a] mb-2">自定义背景图</label>

        {/* 预览(完整显示, 不裁切) */}
        {bgPreview && (
          <div className="mb-4 relative rounded-md overflow-hidden border border-[#d0d7de] bg-[#f6f8fa]">
            <img
              src={bgPreview}
              alt="背景图预览"
              className="w-full max-h-64 object-contain"
            />
            <button
              type="button"
              onClick={() => {
                setBgUrl('');
                setBgPreview('');
              }}
              className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs rounded"
              title="移除背景图"
            >
              移除
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleBgUpload}
            accept="image/*"
            className="hidden"
          />
          <Button
            type="button"
            variant="secondary"
            icon={<FiImage />}
            onClick={() => fileInputRef.current.click()}
          >
            上传图片
          </Button>
          {bgPreview && (
            <Button
              type="button"
              variant="info"
              icon={<FiEye />}
              onClick={() => window.open(bgPreview, '_blank')}
            >
              查看原图
            </Button>
          )}
        </div>

        {/* 或填图片链接 */}
        <div className="mt-3 flex gap-2">
          <div className="relative flex-1">
            <FiLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8b949e]" />
            <input
              type="text"
              value={bgUrl.startsWith('data:image/') ? '' : bgUrl}
              onChange={(e) => {
                setBgUrl(e.target.value);
                setBgPreview(e.target.value);
              }}
              placeholder="或粘贴图片链接 http(s)://..."
              className="w-full bg-[#f6f8fa] rounded-md py-3 pl-9 pr-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none brand-ring"
            />
          </div>
          <Button
            type="button"
            variant="primary"
            icon={<FiSave />}
            onClick={handleSaveBackground}
            disabled={brandSaving}
          >
            {brandSaving ? '保存中...' : '保存背景图'}
          </Button>
        </div>

        <p className="text-sm text-[#8b949e] mt-2">
          上传后自动压缩(最长边1920, 上限4MB); 也可直接填写外链图片地址。背景图仅您自己登录后可见, 不影响其他用户
        </p>
      </div>
    </div>
  );
};

export default BrandPanel;
